import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { usePaymentsReport, type UsePaymentsReportFilters } from '../../hooks/usePaymentsReport';
import type {
  PaymentMethod,
  PaymentStatus,
  PaymentsReportItem,
  PaymentsReportResult,
  PaymentsReportSummary,
} from '../../types/reports';
import authService from '../../services/authService';

interface PaymentsFiltersState {
  startDate: string;
  endDate: string;
  status: PaymentStatus[];
  paymentMethod: Exclude<PaymentMethod, null>[];
  minAmount: string;
  maxAmount: string;
  pedidoId: string;
  providerPaymentId: string;
  consumerId: string;
  cnpj: string;
  limit: number;
}

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const PAYMENT_STATUS_OPTIONS: { value: PaymentStatus; label: string; description: string }[] = [
  { value: 'PENDING', label: 'Pendente', description: 'Pagamento criado e aguardando confirmação' },
  { value: 'APPROVED', label: 'Aprovado', description: 'Pagamento concluído com sucesso' },
  { value: 'REJECTED', label: 'Rejeitado', description: 'Pagamento recusado pelo provedor' },
  { value: 'CANCELLED', label: 'Cancelado', description: 'Pagamento cancelado pelo cliente ou sistema' },
  { value: 'EXPIRED', label: 'Expirado', description: 'Pagamento não confirmado dentro do prazo' },
];

const PAYMENT_METHOD_OPTIONS: { value: Exclude<PaymentMethod, null>; label: string }[] = [
  { value: 'PIX', label: 'PIX' },
  { value: 'CREDIT_CARD', label: 'Cartão de crédito' },
  { value: 'DEBIT_CARD', label: 'Cartão de débito' },
  { value: 'OTHER', label: 'Outros' },
];

const STATUS_COLOR: Record<PaymentStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

const PAYMENT_METHOD_LABEL: Record<Exclude<PaymentMethod, null>, string> = {
  PIX: 'PIX',
  CREDIT_CARD: 'Cartão de crédito',
  DEBIT_CARD: 'Cartão de débito',
  OTHER: 'Outros',
};

const PaymentsReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<PaymentsFiltersState>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      startDate: formatDateInput(firstDay),
      endDate: formatDateInput(now),
      status: ['APPROVED'],
      paymentMethod: [],
      minAmount: '',
      maxAmount: '',
      pedidoId: '',
      providerPaymentId: '',
      consumerId: '',
      cnpj: '',
      limit: 25,
    };
  });

  const user = useMemo(() => authService.getUser(), []);

  const queryFilters = useMemo<UsePaymentsReportFilters>(() => {
    const payload: UsePaymentsReportFilters = {};

    if (filters.startDate) {
      payload.startDate = new Date(`${filters.startDate}T00:00:00`).toISOString();
    }
    if (filters.endDate) {
      payload.endDate = new Date(`${filters.endDate}T23:59:59`).toISOString();
    }
    if (filters.status.length > 0) {
      payload.status = filters.status;
    }
    if (filters.paymentMethod.length > 0) {
      payload.paymentMethod = filters.paymentMethod;
    }
    if (filters.pedidoId.trim()) {
      payload.pedidoId = filters.pedidoId.trim();
    }
    if (filters.providerPaymentId.trim()) {
      payload.providerPaymentId = filters.providerPaymentId.trim();
    }
    if (filters.consumerId.trim()) {
      payload.consumerId = filters.consumerId.trim();
    }
    if (filters.minAmount.trim()) {
      const min = Number(filters.minAmount.replace(',', '.'));
      if (!Number.isNaN(min)) {
        payload.minAmount = min;
      }
    }
    if (filters.maxAmount.trim()) {
      const max = Number(filters.maxAmount.replace(',', '.'));
      if (!Number.isNaN(max)) {
        payload.maxAmount = max;
      }
    }
    const cnpjInput = filters.cnpj.trim();
    const effectiveCnpj = cnpjInput || user?.cnpj || '';
    if (effectiveCnpj) {
      payload.cnpj = effectiveCnpj.replace(/\D/g, '');
    }
    payload.limit = filters.limit;

    return payload;
  }, [filters, user?.cnpj]);

  const {
    data,
    isLoading,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = usePaymentsReport(queryFilters, true);

  const pages = useMemo<PaymentsReportResult[]>(() => data?.pages ?? [], [data]);
  const summary: PaymentsReportSummary | undefined = pages[0]?.summary;
  const items = useMemo<PaymentsReportItem[]>(() => pages.flatMap((page) => page.items ?? []), [pages]);

  const statusSummaryEntries = useMemo(
    () =>
      (summary?.byStatus ? Object.entries(summary.byStatus) : []) as Array<[PaymentStatus, number]>,
    [summary],
  );

  const methodSummaryEntries = useMemo(
    () => (summary?.byMethod ? Object.entries(summary.byMethod) : []) as Array<[string, number]>,
    [summary],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (_event: React.MouseEvent<HTMLElement>, newValue: string[]) => {
    const parsed = (newValue ?? []) as PaymentStatus[];
    setFilters((prev) => ({ ...prev, status: parsed }));
  };

  const handleMethodChange = (_event: React.MouseEvent<HTMLElement>, newValue: string[]) => {
    const sanitized = (newValue ?? []).filter((method): method is Exclude<PaymentMethod, null> => method != null);
    setFilters((prev) => ({ ...prev, paymentMethod: sanitized }));
  };

  const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = Number(event.target.value);
    setFilters((prev) => ({ ...prev, limit: Number.isNaN(parsed) ? prev.limit : parsed }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleNavigateToPrint = () => {
    const params = new URLSearchParams();
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    filters.status.forEach((status) => params.append('status', status));
    filters.paymentMethod.forEach((method) => params.append('paymentMethod', method));
    if (filters.pedidoId.trim()) params.set('pedidoId', filters.pedidoId.trim());
    if (filters.providerPaymentId.trim()) params.set('providerPaymentId', filters.providerPaymentId.trim());
    if (filters.consumerId.trim()) params.set('consumerId', filters.consumerId.trim());
    if (filters.minAmount.trim()) params.set('minAmount', filters.minAmount.trim());
    if (filters.maxAmount.trim()) params.set('maxAmount', filters.maxAmount.trim());
    const cnpjInput = filters.cnpj.trim();
    const effectiveCnpj = cnpjInput || user?.cnpj || '';
    if (effectiveCnpj) params.set('cnpj', effectiveCnpj.replace(/\D/g, ''));

    navigate(`/reports/payments/print?${params.toString()}`);
  };

  const renderSummaryCards = () => (
    <Box
      className="screen-only"
      display="grid"
      gap={2}
      gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }}
    >
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Pagamentos
          </Typography>
          <Typography variant="h4">{summary?.totalPayments ?? 0}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Valor total
          </Typography>
          <Typography variant="h4">{formatCurrency(summary?.totalAmount ?? 0)}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Valor aprovado
          </Typography>
          <Typography variant="h4">{formatCurrency(summary?.approvedAmount ?? 0)}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Ticket médio
          </Typography>
          <Typography variant="h4">{formatCurrency(summary?.averageTicket ?? 0)}</Typography>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStatusDistribution = () => (
    <Box
      className="screen-only"
      display="grid"
      gap={2}
      gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
    >
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Distribuição por status
          </Typography>
          {statusSummaryEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum dado disponível para o período filtrado.
            </Typography>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {statusSummaryEntries.map(([status, count]) => {
                const option = PAYMENT_STATUS_OPTIONS.find((item) => item.value === status);
                return (
                  <Chip
                    key={status}
                    label={`${option?.label ?? status}: ${count}`}
                    color={STATUS_COLOR[status]}
                  />
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Distribuição por método de pagamento
          </Typography>
          {methodSummaryEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum dado agrupado por método.
            </Typography>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {methodSummaryEntries.map(([method, count]) => (
                <Chip
                  key={method}
                  label={`${PAYMENT_METHOD_LABEL[method as Exclude<PaymentMethod, null>] ?? method}: ${count}`}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderPaymentsTable = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Pagamentos detalhados</Typography>
          {isFetching && !isFetchingNextPage && <CircularProgress size={24} />}
        </Box>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum pagamento encontrado com os filtros selecionados.
          </Typography>
        ) : (
          <Box overflow="auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data de criação</TableCell>
                  <TableCell>Pedido</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>ID do provedor</TableCell>
                  <TableCell>Atualizado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                    <TableCell>{item.pedidoId}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          PAYMENT_STATUS_OPTIONS.find((option) => option.value === item.status)?.label ?? item.status
                        }
                        color={STATUS_COLOR[item.status]}
                        variant={item.status === 'APPROVED' ? 'filled' : 'outlined'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {item.paymentMethod
                        ? PAYMENT_METHOD_LABEL[item.paymentMethod as Exclude<PaymentMethod, null>] ?? item.paymentMethod
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                    <TableCell>{item.customerPhone ?? '—'}</TableCell>
                    <TableCell>{item.customerEmail ?? '—'}</TableCell>
                    <TableCell>{item.providerPaymentId ?? '—'}</TableCell>
                    <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
          {hasNextPage && (
            <Button
              variant="outlined"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Carregando...' : 'Carregar mais'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        className="screen-only"
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Relatório de pagamentos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Analise os pagamentos recebidos por período, status, canal de cobrança e valores. Explore os detalhes para
            conciliar pedidos e transações.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleNavigateToPrint}
            disabled={isLoading || isFetching}
          >
            Imprimir
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      <Card className="screen-only">
        <CardContent>
          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }}
          >
            <Box>
              <TextField
                label="Data inicial"
                name="startDate"
                type="date"
                value={filters.startDate}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Data final"
                name="endDate"
                type="date"
                value={filters.endDate}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="ID do pedido"
                name="pedidoId"
                value={filters.pedidoId}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Ex: PED-123"
              />
            </Box>
            <Box>
              <TextField
                label="ID do pagamento"
                name="providerPaymentId"
                value={filters.providerPaymentId}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="ID da adquirente"
              />
            </Box>
            <Box>
              <TextField
                label="Consumer ID"
                name="consumerId"
                value={filters.consumerId}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Telefone ou documento"
              />
            </Box>
            <Box>
              <TextField
                label="Valor mínimo"
                name="minAmount"
                type="number"
                value={filters.minAmount}
                onChange={handleNumericChange}
                fullWidth
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <Box>
              <TextField
                label="Valor máximo"
                name="maxAmount"
                type="number"
                value={filters.maxAmount}
                onChange={handleNumericChange}
                fullWidth
                size="small"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <Box>
              <TextField
                label="CNPJ"
                name="cnpj"
                value={filters.cnpj}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Somente números"
                helperText="Visível apenas para usuários desenvolvedores"
              />
            </Box>
            <Box>
              <TextField
                label="Itens por página"
                name="limit"
                select
                SelectProps={{ native: true }}
                value={filters.limit}
                onChange={handleLimitChange}
                fullWidth
                size="small"
              >
                {[25, 50, 100].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </TextField>
            </Box>
            <Box gridColumn={{ xs: '1 / -1', md: 'span 3' }}>
              <Typography variant="subtitle1">Status do pagamento</Typography>
              <Typography variant="body2" color="text.secondary">
                Selecione os status para filtrar. Nenhum selecionado significa todos os status.
              </Typography>
              <ToggleButtonGroup
                value={filters.status as unknown as string[]}
                onChange={handleStatusChange}
                color="primary"
                size="small"
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                {PAYMENT_STATUS_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none', m: 0.5 }}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {filters.status.length === 0 ? (
                  <Chip label="Todos os status" variant="outlined" />
                ) : (
                  filters.status.map((status) => {
                    const option = PAYMENT_STATUS_OPTIONS.find((item) => item.value === status);
                    return <Chip key={status} label={option?.label ?? status} color={STATUS_COLOR[status]} />;
                  })
                )}
              </Box>
            </Box>
            <Box gridColumn="1 / -1">
              <Typography variant="subtitle1">Métodos de pagamento</Typography>
              <Typography variant="body2" color="text.secondary">
                Deixe vazio para considerar todos os métodos disponíveis.
              </Typography>
              <ToggleButtonGroup
                value={filters.paymentMethod as unknown as string[]}
                onChange={handleMethodChange}
                color="primary"
                size="small"
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                {PAYMENT_METHOD_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none', m: 0.5 }}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {filters.paymentMethod.length === 0 ? (
                  <Chip label="Todos os métodos" variant="outlined" />
                ) : (
                  filters.paymentMethod.map((method) => {
                    const option = PAYMENT_METHOD_OPTIONS.find((item) => item.value === method);
                    return <Chip key={method} label={option?.label ?? method} />;
                  })
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
        <Divider />
      </Card>

      {isError && (
        <Alert severity="error">
          Não foi possível carregar o relatório de pagamentos. Ajuste os filtros ou tente novamente em instantes.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {renderSummaryCards()}
          {renderStatusDistribution()}
          {renderPaymentsTable()}
        </>
      )}
    </Box>
  );
};

export default PaymentsReportPage;
